import { Dispatch } from 'redux';

import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { CreateSblConsentRequestWeb, SblConsentInfoWeb } from '../generated-types';
import { AppState } from '../reducers';
import { ContextReducer } from '../reducers/context';
import { LoaderReducer } from '../reducers/loader';
import { customFailed } from '../util/common';
import { errorNames } from '../util/mappings/errorName';
import parseUrlParams from '../util/urlParams';
import { openStep } from './steps/steps';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

const RedirectUrl = (): string => {
    if (runningInRNWebView) {
        return `${window.location.origin}/sbl-mobilbank-redirect`;
    }
    let baseUrl = window.location.href;
    const hashIndex = baseUrl.indexOf('#');
    if (hashIndex > -1) {
        baseUrl = baseUrl.substring(0, hashIndex);
    }
    return encodeURI(baseUrl);
};

type Action<T> = import('./types').Action<T>;

export interface CreateSblConsentStartedAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_STARTED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_STARTED;
}

export interface CreateSblConsentFulfilledAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_FULFILLED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_FULFILLED;
    payload: SblConsentInfoWeb;
}

export interface CreateSblConsentFailedAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_FAILED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchSblConsentStartedAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_STARTED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_STARTED;
}

export interface FetchSblConsentFulfilledAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_FULFILLED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_FULFILLED;
    payload: SblConsentInfoWeb;
}

export interface FetchSblConsentFailedAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_FAILED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
}

export type SblActions =
    | CreateSblConsentStartedAction
    | CreateSblConsentFailedAction
    | CreateSblConsentFulfilledAction
    | FetchSblConsentStartedAction
    | FetchSblConsentFulfilledAction
    | FetchSblConsentFailedAction;

const createSblConsentStarted = (): CreateSblConsentStartedAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_STARTED,
});

const createSblConsentFailed = (e: RequestExceptionWithResponse): CreateSblConsentFailedAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_FAILED,
    payload: e,
});

const createSblConsentFulfilled = (json: SblConsentInfoWeb): CreateSblConsentFulfilledAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_FULFILLED,
    payload: json,
});

const fetchSblConsentStarted = (): FetchSblConsentStartedAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_STARTED,
});

const fetchSblConsentFailed = (e: RequestExceptionWithResponse): FetchSblConsentFailedAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_FAILED,
    payload: e,
});

const fetchSblConsentFulfilled = (json: SblConsentInfoWeb): FetchSblConsentFulfilledAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_FULFILLED,
    payload: json,
});

export const getOrCreateSblConsent =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, user, context, sbl } = getState();
        const { lastName } = user.context;
        if (!loader.isCreatingSblConsent && lastName && (!loader.hasFetchedSblConsent || sbl.sblStatus === 'REJECTED'))
            try {
                const request: CreateSblConsentRequestWeb = {
                    redirectUrl: RedirectUrl(),
                    lastName,
                };

                dispatch(createSblConsentStarted());
                const response: SblConsentInfoWeb = await post(
                    `${actionTypes.RESOURCE_PATH}/sbl/get-or-create`,
                    request,
                    getRequestHeaders(context.config.bankregnr),
                );

                dispatch(createSblConsentFulfilled(response));

                if (response.sblStatus === 'ACCEPTED') {
                    dispatch(openStep(3));
                }
            } catch (e) {
                dispatch(createSblConsentFailed(e));
            }
    };

export const getSblConsent = async (loader: LoaderReducer, context: ContextReducer, dispatch: Dispatch<AppActions>) => {
    if (!loader.isFetchingSblConsent && !loader.hasFetchedSblConsent)
        try {
            dispatch(fetchSblConsentStarted());
            const response: SblConsentInfoWeb = await get(
                `${actionTypes.RESOURCE_PATH}/sbl/get`,
                getRequestHeaders(context.config.bankregnr),
            );

            const urlParams = parseUrlParams();

            dispatch(fetchSblConsentFulfilled(response));
            if (response.sblStatus === 'ACCEPTED' && urlParams?.Status === 'OK') {
                dispatch(openStep(3));
            }
        } catch (e) {
            dispatch(fetchSblConsentFailed(e));
        }
};

export const sblFailed =
    () =>
    async (dispatch: Dispatch<AppActions>): Promise<void> => {
        dispatch(createSblConsentFailed(customFailed(errorNames.internalError)));
    };
